import { tracked } from '@glimmer/tracking';

import type { APIData } from 'webapp-types';
import { GranulationTypeId, TimeUnit } from 'webapp/enums/enums';

import DateRange from 'webapp/models-custom/date-range';
import MetricLimitationBase from 'webapp/models-custom/metric-limitation/base';
import MetricLimitationGranulation from 'webapp/models-custom/metric-limitation/granulation';

// for more general info check MetricLimitationBase

export default class MetricLimitationRange extends MetricLimitationBase<APIData.Metrics.RangeLimitation> {
	override childLimitations: MetricLimitationGranulation[] = [];

	constructor(
		id: string,
		rawData?: APIData.Metrics.RangeLimitation | null,
		isParentDisabled = false,
		isNumber = false,
		getGranularityConstraints: () => GranulationTypeId[] = () => [],
	) {
		super(id, rawData, isParentDisabled);
		this.createChildLimitations(isNumber, getGranularityConstraints);
	}

	get disabled(): boolean {
		let disabled = false;

		// specifically marked as disabled, or all granularities disabled
		disabled =
			this.hardDisabled ||
			!this.childLimitations.some((granulationLimitation) => {
				return !granulationLimitation.disabled;
			});

		return disabled;
	}

	get hardDisabled(): boolean {
		// specifically marked as disabled, or selected disabled multiplier
		let disabled = !!this.rawData?.disabled;

		if (
			!disabled &&
			this.rawData?.allowedMultipliers &&
			this.chosenRangeMultiplier &&
			!this.rawData.allowedMultipliers.includes(this.chosenRangeMultiplier)
		) {
			disabled = true;
		}

		return disabled;
	}

	get infoText(): string {
		const info: string[] = [];

		if (this.rawData?.info?.message) {
			info.push(this.rawData.info.message);
		} else if (
			this.isPaidLimitation ||
			(this.childLimitations.length === 1 && this.childLimitations[0]!.infoText)
		) {
			info.push(this.childLimitations[0]!.infoText);
		} else if (this.id === 'AllTime' && !this.disabled) {
			// if enabled, always show this text on All Time
			info.push(
				'This date range shows all data that has been synced from your data source and is available on your current plan.',
			);
			// } else {
			// 	// outputs granulation info texts
			// 	this.childLimitations.forEach((granulationLimitation) => {
			// 		if (granulationLimitation.infoText) {
			// 			// don't show base constraint texts on range info
			// 			if (!granulationLimitation.unsupportedBaseConstraint) {
			// 				info.push(`[${granulationLimitation.id}] ${granulationLimitation.infoText}`);
			// 			}
			// 		}
			// 	});
		}

		return info.join('<br>');
	}

	// this can be set for "last x" limitations
	@tracked _chosenRangeMultiplier: number | null = null;
	get chosenRangeMultiplier(): number | null {
		return this._chosenRangeMultiplier;
	}

	set chosenRangeMultiplier(value: number | null) {
		this._chosenRangeMultiplier = value;
		this.updateChildLimitationsForHardDisable();
	}

	private updateChildLimitationsForHardDisable(): void {
		// update child limitations, if we have them already created
		if (this.childLimitations) {
			const disabled = this.hardDisabled;
			this.childLimitations.forEach((limitation) => {
				limitation.isParentDisabled = disabled;
			});
		}
	}

	createChildLimitations(
		isNumber = false,
		getGranularityConstraints: () => GranulationTypeId[],
	): void {
		const childLimitations: MetricLimitationGranulation[] = [];

		// creating granularity limitations

		if (isNumber) {
			let rawData;

			// only "Total" granularity is important for Number type
			if (this.rawData?.granulations) {
				rawData = this.rawData.granulations.Total;
			} else if (this.hardDisabled) {
				// show message from parent (range)
				rawData = {
					info: {
						type: 2, // TODO: type
						message: this.rawData?.info?.message || 'Date range not available',
					},
				};
			}

			childLimitations.push(
				new MetricLimitationGranulation(
					GranulationTypeId.TOTAL,
					rawData,
					this.hardDisabled,
				),
			);
		} else {
			// base constraints
			const allowedGranulations = getGranularityConstraints();

			DateRange.predefinedGranulations
				.filter((item) => item.unit !== TimeUnit.TOTAL)
				.forEach((predefinedGranularity) => {
					let rawData;

					if (allowedGranulations.includes(predefinedGranularity.id)) {
						if (this.rawData?.granulations) {
							rawData = this.rawData.granulations[predefinedGranularity.id];
						} else if (this.hardDisabled) {
							// show message from parent (range)
							rawData = {
								info: {
									type: 2, // TODO: type
									message:
										this.rawData?.info?.message || 'Date range not available',
								},
							};
						}
					} else {
						// base constraints failed
						rawData = {
							disabled: true,
							unsupportedBaseConstraint: true,
							info: {
								type: 1, // TODO: type
								message: 'Granulation not supported (base limitation)',
							},
						};
					}

					childLimitations.push(
						new MetricLimitationGranulation(
							predefinedGranularity.id,
							rawData,
							this.hardDisabled,
						),
					);
				});
		}

		this.childLimitations = childLimitations;
	}
}
